import {
  Button,
  CardHeader,
  DialogActions,
  DialogContent,
  IconButton,
  RootRef,
  Theme,
  Typography,
  WithStyles,
  createStyles,
  withStyles
} from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { Close, Delete, Edit, RecentActors } from '@material-ui/icons';
import autobind from 'autobind-decorator';
import cx from 'classnames';
import * as Draft from 'draft-js';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { AreYouSureDialog, FormButton, FormDialog, Glue } from 'src/components';
import { SnackbarVariant } from 'src/components/AppSnackbar';
import StoryMedia, { MediaType } from 'src/components/StoryMedia';
import StoryEditor, {
  contentStateFromHtml,
  contentStateToHtml
} from 'src/components/editors/StoryEditor';
import {
  CurrentPersonaValue,
  withCurrentPersona
} from 'src/contexts/CurrentPersonaContext';
import { API } from 'src/definitions';
import AxiosException from 'src/exceptions/AxiosException';
import ValidationException from 'src/exceptions/ValidationException';
import UpdateStoryRequest from 'src/schemas/UpdateStoryRequest';
import { load } from 'src/services/Loader';
import RelmApi from 'src/services/RelmApi';
import { toast } from 'src/services/Toaster';
import ValidationTrait, {
  ValidationTraitState
} from 'src/services/ValidationTrait';
import withDataFromApi, {
  Omit,
  WithDataFromApiProps
} from 'src/services/withDataFromApi';
import { createLandingPageStyles } from 'src/utilities';

// region component styles
const styles = (theme: Theme) =>
  createStyles({
    paper: {
      width: '100%',
      height: '100%',
      overflowY: 'unset',
      maxWidth: '1400px',
      [theme.breakpoints.down('md')]: {
        maxWidth: '100%',
        maxHeight: '100%',
        borderRadius: 0,
        margin: 0
      }
    },
    typographyTitle: {
      textAlign: 'center'
    },
    dialogHeader: {
      paddingTop: `${theme.spacing()}px`,
      paddingBottom: `${theme.spacing(0.5)}px`,
      flex: '0 0 auto'
    },
    dialogContent: {
      paddingTop: `${theme.spacing(3)}px`,
      paddingBottom: `${theme.spacing(2)}px`,
      paddingLeft: 0,
      paddingRight: 0,
      justifyContent: 'center',
      backgroundColor: theme.palette.background.paper,
      width: '100%'
    },
    storyContainer: {
      'width': '100%',
      'maxWidth': '1000px',
      'margin': '0 auto',
      'overflow': 'hidden',
      '& > div': {
        margin: 0
      }
    },
    storyTitle: {
      'fontSize': '1.5rem',
      'fontWeight': 700,
      'letterSpacing': '-1px',
      '& > input': {
        margin: 0,
        height: '1.5em',
        padding: `${theme.spacing(1.5)}px ${theme.spacing(4)}px`
      }
    },
    storyEditorContainer: {
      padding: `${theme.spacing(2.4)}px`,
      clear: 'both',
      overflow: 'hidden'
    },
    storySummary: {
      '& > textarea': {
        margin: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
        lineHeight: '1.8em'
      }
    },
    storySummaryLabel: {
      paddingTop: `${theme.spacing(2)}px`,
      left: `${theme.spacing(2)}px`
    },
    storySummaryWarning: {
      textAlign: 'right',
      padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
      color: theme.palette.text.secondary
    },
    storySummaryBad: {
      fontWeight: 'bold',
      color: theme.palette.text.primary
    },
    dialogActions: {
      padding: `${theme.spacing()}px ${theme.spacing(2)}px`
    },
    buttonProfileActionLabel: {
      [theme.breakpoints.down('md')]: {
        display: 'none'
      }
    },
    iconProfileAction: {
      [theme.breakpoints.up('md')]: {
        paddingRight: `${theme.spacing(0.5)}px`
      }
    },
    dialogActionsUpdate: {
      marginRight: `${theme.spacing()}px`,
      paddingTop: `${theme.spacing(0.5)}px`,
      paddingBottom: `${theme.spacing(0.5)}px`
    },
    dialogActionsProcess: {
      marginRight: `${theme.spacing()}px`,
      paddingTop: `${theme.spacing(0.5)}px`,
      paddingBottom: `${theme.spacing(0.5)}px`,
      minWidth: 'auto'
    }
  });

// endregion
// region component props
interface ExternalProps {
  classes?: Partial<ClassNameMap<keyof typeof styles>>;

  /**
   * The `Story` that is being edited.
   */
  story: API.Entities.Story;
  /**
   * Controls the open state of the `Dialog`.
   */
  open: boolean;

  /**
   * Read only mode.
   */
  readOnly: boolean;

  /**
   * Called when the Dialog is closed.
   *
   * @param {boolean} wasUpdated `true` if the `Story` was updated, otherwise `false`.
   */
  onClose: (wasUpdated: boolean) => void;
}

type InternalProps = Required<ExternalProps>;

type Props = InternalProps &
  CurrentPersonaValue &
  WithDataFromApiProps<
    'sellerStyles',
    API.Sellers.ShowSingleSellerStyles.Response
  > &
  WithStyles<typeof styles> &
  RouteComponentProps<{ personaId: string }>;

interface State
  extends ValidationTraitState<
    Omit<API.Stories.UpdateStory.Request, 'position'>
  > {
  isRemoveStoryDialogOpen: boolean;
  editorState: Draft.EditorState;
  imageFile: API.Nullable<File>;
}

// endregion

/**
 *
 */
@withCurrentPersona<Props>()
@withDataFromApi<Props>(
  props => RelmApi.getSellerStyles(props.currentPersona.sellerId),
  'sellerStyles'
)
class EditStoryDialog extends React.Component<Props, State> {
  static readonly defaultProps = {
    currentPersona: undefined,
    onCurrentPersonaChange: undefined,
    sellerStyles: undefined,
    landingPageStyles: undefined,
    readOnly: false,

    onCurrentSellerChange: () => {},
    loadFromApi: undefined
  };

  private readonly _validator = new ValidationTrait(this, UpdateStoryRequest);

  readonly state: State = {
    isRemoveStoryDialogOpen: false,
    editorState: Draft.EditorState.createWithContent(
      contentStateFromHtml(this.props.story.body)
    ),
    imageFile: null,
    inputValues: { ...this.props.story },
    inputErrors: {
      title: '',
      summary: '',
      imageUrl: '',
      videoUrl: '',
      body: '',
      stepId: ''
    }
  };

  private readonly _scrollOffsetRef = React.createRef<HTMLElement>();

  private readonly _classesLPStyles = createLandingPageStyles(
    this.props.sellerStyles
  );

  /**
   * Tries to remove the `Story` with the given id.
   *
   * @param {string} storyId
   */
  async tryRemoveStory(storyId: string) {
    try {
      await RelmApi.removeStory(this.props.story.personaId, storyId);
    } catch (error) {
      if (
        !(error instanceof AxiosException) &&
        !(error instanceof ValidationException)
      ) {
        throw error;
      }

      toast(
        SnackbarVariant.ERROR,
        'Something went wrong while trying to delete this story'
      );

      return;
    }

    this.props.onClose(true);

    toast(SnackbarVariant.SUCCESS, 'Successfully removed story');
  }

  /**
   * Attempts to upload an image file from the state.
   *
   * If the `imageFile` is `null`, or an error occurs while uploading the file,
   * `null` is returned. Otherwise, the `url` of the file is returned.
   *
   * @return {Promise<API.Nullable<API.Entities.File['url']>>}
   */
  async tryUploadImageFromState(): Promise<
    API.Nullable<API.Entities.File['url']>
  > {
    if (!this.state.imageFile) {
      return null;
    }

    try {
      return (await RelmApi.uploadFile(this.state.imageFile)).data.url;
    } catch (error) {
      AxiosException.throwIfNotOneOfUs(error);

      toast(
        SnackbarVariant.ERROR,
        'Something went wrong when trying to upload your image.'
      );

      return null;
    }
  }

  async tryUpdateStory() {
    const imageUrl =
      (await this.tryUploadImageFromState()) ?? this.state.inputValues.imageUrl;

    try {
      await RelmApi.updateStory(
        this.props.story.personaId,
        this.props.story.id,
        {
          ...this.state.inputValues,
          imageUrl
        }
      );
    } catch (error) {
      if (error instanceof AxiosException) {
        toast(
          SnackbarVariant.ERROR,
          'Something went wrong when trying to update story'
        );

        return;
      }

      this._validator.handlePossibleValidationError(error);

      return;
    }

    toast(SnackbarVariant.SUCCESS, 'Successfully updated story');

    this.props.onClose(true);
  }

  // region autobound methods
  @autobind
  handleSaveStoryButtonClick() {
    load(this.tryUpdateStory());
  }

  /**
   * Handles when the `'Delete'` action button is clicked.
   */
  @autobind
  handleRemoveStoryButtonClick() {
    this.setState({ isRemoveStoryDialogOpen: true });
  }

  /**
   * Handles when the `'Cancel'` button on the `AreYouSureDialog` for deleting a `Story` is clicked.
   */
  @autobind
  handleRemoveStoryCancel() {
    this.setState({ isRemoveStoryDialogOpen: false });
  }

  /**
   * Handles when the `'Action'` button on the `AreYouSureDialog` for deleting a `Story` is clicked.
   */
  @autobind
  handleRemoveStoryAction() {
    load(this.tryRemoveStory(this.props.story.id));
  }

  @autobind
  handleCloseClick() {
    this.props.onClose(false);
  }

  @autobind
  handleBodyEditorChange(editorState: Draft.EditorState) {
    this.setState({
      editorState,
      inputValues: {
        ...this.state.inputValues,
        body: contentStateToHtml(editorState.getCurrentContent())
      }
    });
  }

  /**
   * Handles when a file is dropped onto the `imageUrl` dropzone.
   *
   * @param {Array<File>} accepted
   */
  @autobind
  handleImageUrlFileDrop(accepted: File[]) {
    this.setState({ imageFile: accepted[0] });
  }

  @autobind
  handleClearMediaClick(mediaType: MediaType) {
    const imageFile =
      mediaType === MediaType.IMAGE ? null : this.state.imageFile;

    this.setState({
      inputValues: {
        ...this.state.inputValues,
        [mediaType]: null
      },
      imageFile
    });
  }

  /**
   * Handle
   */
  @autobind
  handleGoToPersonaClick() {
    if (this.props.story.personaId === null) {
      throw new Error('no persona for this consumer');
    } // don't do anything if the steps are the same
    const personaUrl = `/p/${this.props.story.personaId}/stories`;

    if (this.props.location.toString() !== personaUrl) {
      this.props.history.push(personaUrl);
    }
  }

  // endregion
  // region methods
  summaryWarn() {
    const nbCaractersLeft = 20;

    if (
      !this.state.inputValues.summary ||
      this.state.inputValues.summary === ''
    ) {
      return;
    }
    const charactersLeft: number = 255 - this.state.inputValues.summary.length;

    if (charactersLeft > nbCaractersLeft) {
      return;
    }

    return (
      <Typography
        className={cx({
          [this.props.classes.storySummaryWarning]:
            charactersLeft <= nbCaractersLeft,
          [this.props.classes.storySummaryBad]: charactersLeft < 0
        })}
      >
        Characters left in the summary: {charactersLeft}
      </Typography>
    );
  }

  // endregion
  // region render & get-render-content methods
  render() {
    if (!this.props.currentPersona) {
      throw new Error('currentPersona not loaded');
    } // shouldn't happen, but TypeScript doesn't realise that
    const classes = this.props.classes;
    const readOnly =
      this.props.readOnly ||
      this.props.story.personaId !== this.props.currentPersona.id;
    const headerTitle = `${readOnly ? 'Read' : 'Edit'} ${
      this.props.story.consumerId ? 'Private' : ''
    } Story`;

    return (
      <FormDialog classes={{ paper: classes.paper }} open={this.props.open}>
        <CardHeader
          className={classes.dialogHeader}
          style={this._classesLPStyles.storyHeader}
          avatar={<RecentActors />}
          action={
            <IconButton onClick={this.handleCloseClick}>
              <Close />
            </IconButton>
          }
          titleTypographyProps={{
            className: classes.typographyTitle
          }}
          title={headerTitle}
        />
        <RootRef rootRef={this._scrollOffsetRef}>
          <DialogContent
            className={classes.dialogContent}
            style={this._classesLPStyles.storyBg}
          >
            <div
              className={classes.storyContainer}
              style={this._classesLPStyles.storyContainer}
            >
              <StoryMedia
                imageFile={this.state.imageFile}
                imageUrl={this.state.inputValues.imageUrl}
                videoUrl={this.state.inputValues.videoUrl}
                videoError={this.state.inputErrors.videoUrl}
                readOnly={readOnly}
                onFileDropped={this.handleImageUrlFileDrop}
                onClearMediaClick={this.handleClearMediaClick}
              >
                {this._validator.buildTextFieldForProperty(
                  'videoUrl',
                  'Video URL'
                )}
              </StoryMedia>
              {this._validator.buildTextFieldForProperty('title', '', {
                required: true,
                placeholder: 'Title',
                InputProps: {
                  classes: {
                    root: classes.storyTitle
                  },
                  style: this._classesLPStyles.storyTitle,
                  readOnly
                }
              })}
              <StoryEditor
                className={classes.storyEditorContainer}
                editorState={this.state.editorState}
                scrollOffsetRef={this._scrollOffsetRef}
                readOnly={readOnly}
                onChange={this.handleBodyEditorChange}
                onCtrlEnter={this.handleSaveStoryButtonClick}
              />
              {this._validator.buildTextFieldForProperty('summary', 'Summary', {
                multiline: true,
                rows: 3,
                InputLabelProps: {
                  classes: {
                    root: classes.storySummaryLabel
                  }
                },
                InputProps: {
                  classes: {
                    root: classes.storySummary
                  },
                  style: this._classesLPStyles.storySummary,
                  readOnly
                }
              })}
            </div>
          </DialogContent>
        </RootRef>
        <DialogActions className={classes.dialogActions}>
          {!readOnly && (
            <>
              <Button
                className={classes.dialogActionsUpdate}
                variant="outlined"
                size="small"
                color="primary"
                onClick={this.handleRemoveStoryButtonClick}
              >
                <Delete className={classes.iconProfileAction} />
                <span className={classes.buttonProfileActionLabel}>Delete</span>
              </Button>
              <Glue />
              {this.summaryWarn()}
              <FormButton
                className={classes.dialogActionsProcess}
                variant="outlined"
                size="large"
                color="primary"
                disabled={
                  this.state.inputValues.title === '' ||
                  (this.state.inputValues.summary !== null &&
                    this.state.inputValues.summary!.length > 255)
                }
                onClick={this.handleSaveStoryButtonClick}
              >
                <Edit className={classes.iconProfileAction} />
                <span className={classes.buttonProfileActionLabel}>Save</span>
              </FormButton>
            </>
          )}
          {readOnly && this.props.story.personaId && (
            <>
              <Glue />
              {!this.props.story.consumerId && (
                <Button
                  className={classes.dialogActionsUpdate}
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={this.handleGoToPersonaClick}
                >
                  <RecentActors className={classes.iconProfileAction} />
                  <span className={classes.buttonProfileActionLabel}>
                    Go to Persona
                  </span>
                </Button>
              )}
            </>
          )}
        </DialogActions>
        <AreYouSureDialog
          open={this.state.isRemoveStoryDialogOpen}
          actionText="Delete"
          onCancel={this.handleRemoveStoryCancel}
          onAction={this.handleRemoveStoryAction}
        >
          Are you sure you want to delete this story?
          <br />
          <strong>This action cannot be undone.</strong>
        </AreYouSureDialog>
      </FormDialog>
    );
  }

  // endregion
}

export default withRouter(withStyles(styles)(EditStoryDialog));
